import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

import UnfurledLinkComponent from './components';
import { UNFURLED_LINK_METADATA_VERSION } from './constants';

const readAttribute = (element: HTMLElement, ...names: string[]) => {
    for (const name of names) {
        const value = element.getAttribute(name);

        if (value !== null) {
            return value;
        }
    }

    return null;
};

const renderDataAttribute = (name: string, value: unknown) => (value ? { [name]: String(value) } : {});

const readBooleanAttribute = (element: HTMLElement, ...names: string[]) => {
    const value = readAttribute(element, ...names);

    if (!value || value === 'false') {
        return false;
    }

    return value === 'true' ? true : value;
};

export const UnfurledLink = Node.create({
    name: 'unfurledLink',

    group: 'block', // Group for block-level nodes (not inline)

    content: '', // Ensure it cannot have children

    atom: true,

    addAttributes() {
        return {
            url: {
                default: null,
                parseHTML: (element) => readAttribute(element, 'data-url', 'url'),
                renderHTML: (attributes) => renderDataAttribute('data-url', attributes.url),
            },
            title: {
                default: null,
                parseHTML: (element) => readAttribute(element, 'data-title', 'title'),
                renderHTML: (attributes) => renderDataAttribute('data-title', attributes.title),
            },
            description: {
                default: null,
                parseHTML: (element) => readAttribute(element, 'data-description', 'description'),
                renderHTML: (attributes) => renderDataAttribute('data-description', attributes.description),
            },
            image: {
                default: null,
                parseHTML: (element) => readAttribute(element, 'data-image', 'image'),
                renderHTML: (attributes) => renderDataAttribute('data-image', attributes.image),
            },
            logo: {
                default: null,
                parseHTML: (element) => readAttribute(element, 'data-logo', 'logo'),
                renderHTML: (attributes) => renderDataAttribute('data-logo', attributes.logo),
            },
            publisher: {
                default: null,
                parseHTML: (element) => readAttribute(element, 'data-publisher', 'publisher'),
                renderHTML: (attributes) => renderDataAttribute('data-publisher', attributes.publisher),
            },
            date: {
                default: null,
                parseHTML: (element) => readAttribute(element, 'data-date', 'date'),
                renderHTML: (attributes) => renderDataAttribute('data-date', attributes.date),
            },
            author: {
                default: null,
                parseHTML: (element) => readAttribute(element, 'data-author', 'author'),
                renderHTML: (attributes) => renderDataAttribute('data-author', attributes.author),
            },
            isLoading: {
                default: null,
                parseHTML: (element) => readBooleanAttribute(element, 'data-is-loading', 'data-isLoading', 'isloading'),
                renderHTML: (attributes) => renderDataAttribute('data-is-loading', attributes.isLoading),
            },
            requestId: {
                default: null,
                parseHTML: (element) => readAttribute(element, 'data-request-id', 'requestid'),
                renderHTML: () => ({}),
            },
            metadataVersion: {
                default: null,
                parseHTML: (element) => readAttribute(element, 'data-metadata-version', 'metadataversion'),
                renderHTML: (attributes) =>
                    renderDataAttribute(
                        'data-metadata-version',
                        attributes.metadataVersion ?? UNFURLED_LINK_METADATA_VERSION,
                    ),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="unfurled-link"]',
            },
            {
                tag: 'unfurled-link',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'unfurled-link' })];
    },

    addNodeView() {
        return ReactNodeViewRenderer(UnfurledLinkComponent);
    },
});
